<template>
  <div class="card">
    <router-link
      v-slot="{ href }"
      :to="productLink"
      custom>
      <a
        :href="href"
        class="card__head"
        @click.prevent="mutateProductAndPush">
        <mouse-shift-parallax
          ref="mouseShiftParallax"
          :base-transforms="{ start: 'translateY(50%)' }"
          :max-parallax-shift="{ x: 7, y: 7 }"
          class="card__head-content"
          :class="{
            'card__head-content--unavailable': !isAvailable,
            'card__head-content--no-image': !images.foreground,
            'card__head-content--has-subscription': monthlySubscription,
          }">
          <template v-if="images.foreground">
            <img
              v-if="isLazy"
              :data-src="images.foreground"
              class="lazyload card__img-fg"
              alt=""
              data-fallback-img="/images/games/picture_item.png"
              @error="_skGlobal.handleFallbackImg">
            <img
              v-else
              :src="images.foreground"
              alt=""
              class="card__img-fg"
              data-fallback-img="/images/games/picture_item.png"
              @error="_skGlobal.handleFallbackImg">
          </template>
          <template #static>
            <img
              v-if="isLazy"
              :data-src="images.background"
              class="lazyload card__img-bg"
              alt=""
              data-fallback-img="/images/games/picture_bg.jpg"
              @error="_skGlobal.handleFallbackImg">
            <img
              v-else
              :src="images.background"
              alt=""
              class="card__img-bg"
              data-fallback-img="/images/games/picture_bg.jpg"
              @error="_skGlobal.handleFallbackImg">
            <p class="card__title">
              {{ productName }}
            </p>
            <div
              v-if="monthlySubscription"
              class="card__subscription">
              <span class="card__subscription-title">4 in 1</span>
              <span class="card__subscription-text">Monthly Subscription</span>
            </div>
            <product-advantages
              v-if="!monthlySubscription && !isGameBanner"
              :product="product"
              class="card__bullits" />
            <div class="card__order">
              <upper-cents-price
                v-if="isAvailable && +oldPrice && oldPrice !== price"
                class="card__price--old"
                :sum="oldPrice" />
              <upper-cents-price
                v-if="isAvailable && price"
                class="card__price"
                :sum="price" />
              <div
                v-if="canBuy && !isGameBanner"
                class="card__button button button-main">
                Order now
              </div>
            </div>
            <div
              v-if="!isAvailable"
              class="card__label">
              sold out
            </div>
          </template>
        </mouse-shift-parallax>
      </a>
    </router-link>
  </div>
</template>

<script>
import UpperCentsPrice from 'Components/ui/Price/UpperCentsPrice.vue';
import { mapState } from 'vuex';
import GameModel from 'Models/GameModel';
import MouseShiftParallax from 'Components/ui/Parallax/MouseShiftParallax.vue';
import ProductAdvantages from 'Components/Products/ProductAdvantages.vue';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import RegionModel from 'Models/RegionModel';
import { flatTagsById } from 'Root/helpers/tags';

export default {
  name: 'ProductCard',

  components: {
    ProductAdvantages,
    MouseShiftParallax,
    UpperCentsPrice,
  },

  props: {
    showFooter: {
      type: Boolean,
      default: false,
    },

    product: {
      type: Object,
      default: () => ({}),
    },

    linkQuery: {
      type: Object,
      default: () => ({}),
    },

    isGameBanner: {
      type: Boolean,
      default: false,
    },

    isLazy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      flatTagsById: flatTagsById ?? {},
    };
  },

  computed: {
    ...mapState(['activeRegion', 'activeGame']),
    ...mapState('products', ['tags']),

    bullits() {
      return this.product.advantages?.filter((a) => !!a) || [];
    },

    // description() {
    //   return getTextFromHTML(this.product.short_description);
    // },

    productName() {
      return this.product.title;
    },

    productId() {
      return this.product.id;
    },

    price() {
      return this.product.initial_prices?.[this.activeRegion.shortTitle.toLowerCase()]
              || this.product[`cost_${this.activeRegion.shortTitle.toLowerCase()}`];
    },

    oldPrice() {
      return +this.product.initial_prices?.[`${this.activeRegion.shortTitle.toLowerCase()}_old`]
        || +this.product[`cost_${this.activeRegion.shortTitle.toLowerCase()}_old`];
    },

    images() {
      const foregroundImage = this.product.picture_item_small || this.product.picture_item;

      return {
        background: this.product.picture_bg ? `/storage${this.product.picture_bg}` : '/images/temp/card-bg.jpg',
        foreground: foregroundImage ? `/storage${foregroundImage}` : null,
      };
    },

    isAvailable() {
      return this.product.available_in_regions?.includes(this.activeRegion.id);
    },

    productSlug() {
      return this.product?.title_slug;
    },

    monthlySubscription() {
      return this.product.meta?.isMonthlySubscription;
    },

    productLink() {
      if (!this.product.show_as_tag?.id) {
        return {
          name: 'game__product-detail',
          params: {
            namespace: GameModel.ID_TO_DATA[this.product.game]?.routeNamespace,
            id: this.productSlug ? `${this.productSlug}-${this.product.id}` : this.product.id,
          },
          query: this.linkQuery,
        };
      }

      return this.flatTagsById[this.product.show_as_tag.id]?._href ?? '';
    },

    canBuy() {
      return this.isAvailable && !(this.product.meta && this.product.meta.unavailableText);
    },
  },

  watch: {
    'images.foreground': {
      handler(newValue, oldValue) {
        if (!newValue) {
          this.$refs.mouseShiftParallax.destroyParallax();
        }

        if (!oldValue && newValue) {
          this.$refs.mouseShiftParallax.initParallax();
        }
      },
    },
  },

  methods: {
    scrollForward(el) {
      el.scrollBy({ behavior: 'smooth', left: 1 });
    },

    scrollBack(el) {
      el.scrollBy({ behavior: 'smooth', left: -1 });
    },

    mutateProductAndPush() {
      this.$store.commit('products/mutateProduct', { ...this.product, _shouldActualize: true });
      this.$router.push(this.productLink);
      this.sendGoogleAnalytics();
    },

    sendGoogleAnalytics() {
      const game = GameModel.ID_TO_DATA[this.product.game].fullTitle;
      const currency = RegionModel.ID_TO_DATA[this.$store.state.activeRegion.id].currency.title;
      googleTagAnalytics.emitProductNavigationNew(game, this.product, this.price, currency);
    },
  },
};
</script>

<style lang="scss" scoped>
  .card {
    --card-height: 100%;

    font-weight: 500;
    height: var(--card-height);

    /* Вложенность для приоритета, нарушен порядок чанков */
    .card__price {
      --font-size: #{$text-25px};
      --font-size--cent: #{$text-11px};
      --cent-margin: 4px 0 0 1px;

      display: block;
      z-index: 2;
      grid-column: 1;
    }

    .card__price--old {
      --font-size: #{$text-15px};
      --color: #{$color--basic-light-gray};
      --text-decoration: line-through;
      --cent-margin: 3px 0 0 1px;

      z-index: 2;
      margin-right: 8px;
      grid-column: 1;
    }
  }

  .card__head {
    position: relative;
    overflow: hidden;
    background-color: $color--basic-menu;
    border-radius: 15px;
    display: block;
    color: $color--basic-white;
    width: 100%;
  }

  .card__head-content {
    position: relative;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-position: center;
    background-size: cover;
    display: grid;
    grid-template-rows: 1fr max-content;
    overflow: hidden;
    z-index: 1;
  }

  .card__img-bg {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 15px;
  }

  .card__img-fg {
    position: absolute;
    z-index: 2;
    width: auto;
    max-width: 60%;
    height: auto;
    max-height: 60%;
    right: 16px;
    bottom: 50%;
    will-change: transform;
    transform: translateY(50%);
  }

  .card__head-content--no-image {
    .card__img-bg {
      opacity: .35;
    }
  }

  .card__head-content--unavailable:after {
    content: '';
    background-color: $color--basic-menu;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    mix-blend-mode: color;
  }

  .card__has-footer {
    display: flex;
    flex-direction: column;

    .card__head {
      border-radius: 15px 15px 0 0;
      flex: 1;
    }
  }

  :not(.card-head__has-footer) .card__head {
    height: 100%;
  }

  .card__title {
    padding: 16px;
    font-size: 13px;
    line-height: 1.2;
    z-index: 3;
  }

  .card__order {
    padding: 16px;
    display: grid;
    align-items: end;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(3, max-content);
    grid-column-gap: 8px;
    grid-row-gap: 2px;
  }

  .card__button {
    text-transform: none;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    height: 40px;
    margin-top: 16px;
    font-size: $text-13px;
    z-index: 2;
  }

  .card__label {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 3;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 0;
    width: 100%;
    background-color: $color--basic-blue;
    text-align: center;
    transform:  translate(35%, -100%) rotate(-45deg);
  }

  .card__subscription {
    position: relative;
    font-size: 11px;
    line-height: 1.2;
    background: linear-gradient(
        270deg,
        rgba($color--basic-blue, 0) 0%,
        $color--basic-blue 30%,
        $color--basic-purple 100%
    );
    width: max-content;
    padding: 4px 4px 4px 16px;
  }

  @media screen and #{$screen-less-than-420} {
    .card__bullits {
      display: none !important;
    }

    .card__head-content--has-subscription {
      grid-template-rows: max-content 1fr max-content;
    }

    .card__subscription {
      margin: 16px 0 -8px;
      grid-row: 1;
    }

    .card__subscription-text {
      margin-left: .5ch;
    }
  }

  @media screen and (min-width: 420px) {
    .card__head.card-head__has-footer {
      height: 248px;
    }

    .card__bullits {
      --background-color: #{$color--basic-black};

      font-size: 11px;
      padding: 12px 16px;
    }

    .card__img-fg {
      right: 0;
      max-width: 65%;
      max-height: 75%;
    }

    .card__head-content {
      grid-template-rows: 1fr 80px 1fr;
    }

    .card__head-content--unavailable {
      grid-template-rows: 1fr 80px max-content;
    }

    .card__button {
      padding: 0 16px;
      height: 48px;
      grid-column: 2;
      grid-row: 1 / -1;
      font-size: $text-15px;
      margin-top: 0;
    }

    .card__order {
      grid-template-columns: 1fr max-content;
      grid-template-rows: 1fr max-content;
      grid-row-gap: 0;
      grid-column-gap: 16px;
    }

    .card__head-content--no-image {
      .card__bullits:before {
        --background-color: transparent;
      }
    }

    .card__subscription {
      background: linear-gradient(270deg, $color--basic-black 46%, $color--basic-blue 87%, $color--basic-purple 100%);
      font-size: 13px;
      padding: 8px 8px 8px 16px;
      font-weight: 500;
      display: grid;
      width: 100%;
    }

    .card__subscription-title {
      font-size: 25px;
    }

    .card__subscription-text {
      width: 90px;
    }
  }
</style>
